import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Flex, Icon, Notification, TextField, toast } from "@telia/teddy";
import { SHSignStatus, SIGN_STATUS_LABELS } from "../../../../../models/server/signatureHandler";
import {
    API_URL_CONFIRM_MANUAL_SIGNATURE_FOR_ORDER,
    API_URL_GET_SIGN_DOCUMENT_STATUS_FOR_ORDER,
    API_URL_GET_SIGNATURE_METHODS_FOR_ORDER,
    API_URL_PRINT_SIGN_DOCUMENTS,
    API_URL_REQUEST_SIGN_DOCUMENT_FOR_ORDER,
} from "../../api";
import { SignatureMethodEnum } from "../../types/enums";
import {
    SIGNATURE_METHOD_LABELS,
    SIGNATURE_METHOD_LABELS_ELKJOP,
    DOCUMENT_TYPE_LABELS,
    getManualSignButtonText,
    getManualSignAriaLabel,
} from "../signatureLabels";

const POLL_INTERVAL_MS = 15000;

const STOP_POLLING_SIGN_STATUSES: SHSignStatus[] = [
    "signed",
    "closed",
    "canceled",
    "rejected",
    "document_error",
    "ssn_validation_failed",
];

const isTerminalSignStatus = (status?: string | null): boolean =>
    !!status && STOP_POLLING_SIGN_STATUSES.includes(status.toLowerCase() as SHSignStatus);

interface SignOrderModalProps {
    omOrderId: number;
    customerPhone?: string | number;
    customerName?: string;
    isElkjop?: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    onRefresh?: () => void;
}

const SignOrderModal: FC<SignOrderModalProps> = ({
    omOrderId,
    customerPhone,
    customerName,
    isElkjop = false,
    onCancel,
    onSuccess,
    onRefresh,
}) => {
    const signatureMethodLabels = isElkjop ? SIGNATURE_METHOD_LABELS_ELKJOP : SIGNATURE_METHOD_LABELS;
    const [isOwnerPresent, setIsOwnerPresent] = useState(true);
    const [methodsFetchInitiated, setMethodsFetchInitiated] = useState(false);
    const [signatureMethods, setSignatureMethods] = useState<Array<{ signature_method: SignatureMethodEnum }>>([]);
    const [isLoadingMethods, setIsLoadingMethods] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRequesting, setIsRequesting] = useState(false);
    const [signStatus, setSignStatus] = useState<string | null>(null);
    const [signCode, setSignCode] = useState<string | null>(null);
    const [manualSignClicked, setManualSignClicked] = useState(false);
    const [hasPrinted, setHasPrinted] = useState(false);
    const [phoneInput, setPhoneInput] = useState(customerPhone ? String(customerPhone) : "");
    const [pendingMethod, setPendingMethod] = useState<SignatureMethodEnum | null>(null);
    const [isAwaitingSigning, setIsAwaitingSigning] = useState(false);
    const [assentlyDocuments, setAssentlyDocuments] = useState<Array<{ id: string; type: string }> | null>(null);
    const [hasOpenedDocuments, setHasOpenedDocuments] = useState(false);
    const [selectedDocIds, setSelectedDocIds] = useState(new Set<string>());
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
    const isPollingActiveRef = useRef(false);

    const stopPolling = useCallback(() => {
        isPollingActiveRef.current = false;
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = undefined;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        isPollingActiveRef.current = true;
        pollIntervalRef.current = setInterval(async () => {
            if (!isPollingActiveRef.current) return;
            try {
                const url = new URL(API_URL_GET_SIGN_DOCUMENT_STATUS_FOR_ORDER, window.location.href);
                url.searchParams.set("omOrderId", String(omOrderId));
                const response = await fetch(url.toString());
                if (!isPollingActiveRef.current) return;
                if (!response.ok) throw new Error("Feil ved henting av signeringsstatus");
                const statusResult = await response.json();
                if (!isPollingActiveRef.current) return;
                setError(null);
                const newStatus = statusResult?.status;
                if (newStatus) {
                    setSignStatus(newStatus);
                    if (isTerminalSignStatus(newStatus)) {
                        isPollingActiveRef.current = false;
                        setIsAwaitingSigning(false);
                        stopPolling();
                        if (newStatus.toLowerCase() === "signed") {
                            toast.success("Ordre er signert", { position: "top-center", duration: 8000 });
                            onSuccess();
                        }
                    }
                }
            } catch {
                if (isPollingActiveRef.current) {
                    setError("Kunne ikke hente signeringsstatus. Vennligst prøv igjen.");
                }
            }
        }, POLL_INTERVAL_MS);
    }, [omOrderId, stopPolling, onSuccess]);

    const fetchMethods = useCallback(async () => {
        setError(null);
        setSignatureMethods([]);
        setIsLoadingMethods(true);
        try {
            const url = new URL(API_URL_GET_SIGNATURE_METHODS_FOR_ORDER, window.location.href);
            url.searchParams.set("omOrderId", String(omOrderId));
            url.searchParams.set("ownerPresent", String(isOwnerPresent));
            const response = await fetch(url.toString());
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.error ?? "Kunne ikke hente signeringsmetoder");
            }
            const result = await response.json();
            setSignatureMethods(result?.signatureMethods ?? []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Kunne ikke hente signeringsmetoder. Vennligst prøv igjen.");
        } finally {
            setIsLoadingMethods(false);
        }
    }, [omOrderId, isOwnerPresent]);

    useEffect(() => {
        if (methodsFetchInitiated) {
            fetchMethods();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwnerPresent]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    const handleMethodSelect = async (method: SignatureMethodEnum) => {
        if (
            method === SignatureMethodEnum.SIGN_ONLY ||
            method === SignatureMethodEnum.REMOTE_ONE_TIME_PIN_AND_BANK_ID ||
            method === SignatureMethodEnum.REMOTE_BANK_ID ||
            method === SignatureMethodEnum.BANK_ID ||
            method === SignatureMethodEnum.ID_PHOTO
        ) {
            setIsRequesting(true);
            setError(null);
            setSignStatus(null);
            try {
                const response = await fetch(API_URL_REQUEST_SIGN_DOCUMENT_FOR_ORDER, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        omOrderId,
                        signatureMethod: method,
                        customerPhone: phoneInput || (customerPhone ? String(customerPhone) : undefined),
                        customerName,
                    }),
                });
                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    throw new Error(body?.error ?? "Kunne ikke sende signeringsforespørsel");
                }
                const result = await response.json();

                // If the order was already signed (SignatureHandler returns 507 → server maps to "signed"),
                // complete immediately rather than entering a stale signing flow.
                if (result?.status?.toLowerCase() === "signed") {
                    toast.success(`Ordre ${omOrderId} er allerede signert`, { position: "top-center", duration: 8000 });
                    onSuccess();
                    return;
                }

                if (method !== SignatureMethodEnum.SIGN_ONLY) {
                    setSignStatus(result?.status ?? null);
                }

                if (result?.documents && result.documents.length > 0) {
                    setAssentlyDocuments(result.documents);
                    setSelectedDocIds(new Set(result.documents.map((d: { id: string }) => d.id)));
                }

                if (
                    (method === SignatureMethodEnum.SIGN_ONLY ||
                        method === SignatureMethodEnum.BANK_ID ||
                        method === SignatureMethodEnum.ID_PHOTO) &&
                    result?.session_id
                ) {
                    const last6 = result.session_id.slice(-6);
                    setSignCode(`${last6.slice(0, 3)}-${last6.slice(3)}`);
                }

                if (
                    method === SignatureMethodEnum.REMOTE_BANK_ID ||
                    method === SignatureMethodEnum.BANK_ID ||
                    method === SignatureMethodEnum.ID_PHOTO ||
                    method === SignatureMethodEnum.REMOTE_ONE_TIME_PIN_AND_BANK_ID ||
                    method === SignatureMethodEnum.SIGN_ONLY
                ) {
                    setIsAwaitingSigning(!isTerminalSignStatus(result?.status ?? null));
                }
                startPolling();
            } catch (e: unknown) {
                setError(
                    e instanceof Error ? e.message : "Kunne ikke sende signeringsforespørsel. Vennligst prøv igjen.",
                );
            } finally {
                setIsRequesting(false);
            }
        }
    };

    const handleBack = () => {
        stopPolling();
        setError(null);
        setSignStatus(null);
        setSignCode(null);
        setIsRequesting(false);
        setIsAwaitingSigning(false);
        setAssentlyDocuments(null);
        setSelectedDocIds(new Set());
        setHasPrinted(false);
        setPendingMethod(null);
        setHasOpenedDocuments(false);
    };

    const isSigningInProgress = !!signStatus || !!signCode || isAwaitingSigning;

    return (
        <div className="orders__sign-form">
            <Flex direction="column" gap="200">
                {!!error && (
                    <Notification type="tile" variant="error" mt="100">
                        <Notification.Icon />
                        <Notification.Text>{error}</Notification.Text>
                    </Notification>
                )}

                {!isSigningInProgress && !manualSignClicked && (
                    <>
                        {!isElkjop && (
                            <Checkbox className="sign-order-modal__owner-present-checkbox">
                                <Checkbox.Trigger
                                    id={`owner-present-${omOrderId}`}
                                    checked={isOwnerPresent}
                                    onCheckedChange={(checked) => setIsOwnerPresent(!!checked)}
                                />
                                <span>Eier er tilstede</span>
                            </Checkbox>
                        )}

                        {!methodsFetchInitiated && (
                            <Button
                                variant="primary"
                                size="sm"
                                mt={isElkjop ? "300" : "100"}
                                style={{ width: "fit-content" }}
                                onClick={() => {
                                    setMethodsFetchInitiated(true);
                                    fetchMethods();
                                }}
                            >
                                Hent signeringsmetoder
                            </Button>
                        )}
                    </>
                )}

                {isLoadingMethods && <p>Henter signeringsmetoder...</p>}

                {!isLoadingMethods && !error && signatureMethods.length === 0 && methodsFetchInitiated && (
                    <p>Ingen signeringsmetoder tilgjengelig.</p>
                )}

                {!isLoadingMethods &&
                    !error &&
                    signatureMethods.length > 0 &&
                    !manualSignClicked &&
                    !isSigningInProgress && (
                        <Flex direction="column" gap="100">
                            {signatureMethods.map((method) =>
                                method.signature_method === SignatureMethodEnum.MANUAL_SIGN ? (
                                    <Button
                                        key={method.signature_method}
                                        variant="secondary"
                                        size="sm"
                                        style={{ width: "fit-content" }}
                                        aria-label={getManualSignAriaLabel(isElkjop, true)}
                                        loading={isRequesting}
                                        onClick={async () => {
                                            setIsRequesting(true);
                                            setError(null);
                                            try {
                                                const response = await fetch(API_URL_REQUEST_SIGN_DOCUMENT_FOR_ORDER, {
                                                    method: "POST",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({
                                                        omOrderId,
                                                        signatureMethod: "MANUAL_SIGN",
                                                        customerPhone:
                                                            phoneInput ||
                                                            (customerPhone ? String(customerPhone) : undefined),
                                                        customerName,
                                                    }),
                                                });
                                                if (!response.ok) {
                                                    const body = await response.json().catch(() => ({}));
                                                    throw new Error(
                                                        body?.error ?? "Kunne ikke starte signeringsprosess",
                                                    );
                                                }
                                                const result = await response.json();
                                                if (result?.documents && result.documents.length > 0) {
                                                    setAssentlyDocuments(result.documents);
                                                    setSelectedDocIds(
                                                        new Set(result.documents.map((d: { id: string }) => d.id)),
                                                    );
                                                }
                                                setManualSignClicked(true);
                                            } catch (e: unknown) {
                                                setError(
                                                    e instanceof Error
                                                        ? e.message
                                                        : "Kunne ikke starte signeringsprosess. Vennligst prøv igjen.",
                                                );
                                            } finally {
                                                setIsRequesting(false);
                                            }
                                        }}
                                    >
                                        <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                        {getManualSignButtonText(isElkjop)}
                                    </Button>
                                ) : (
                                    <Button
                                        key={method.signature_method}
                                        variant="secondary"
                                        size="sm"
                                        style={{ width: "fit-content" }}
                                        onClick={() => {
                                            if (
                                                !isOwnerPresent &&
                                                (method.signature_method === SignatureMethodEnum.REMOTE_BANK_ID ||
                                                    method.signature_method ===
                                                        SignatureMethodEnum.REMOTE_ONE_TIME_PIN_AND_BANK_ID)
                                            ) {
                                                setPendingMethod(method.signature_method);
                                            } else {
                                                handleMethodSelect(method.signature_method);
                                            }
                                        }}
                                        loading={isRequesting}
                                        disabled={isRequesting || !!signStatus}
                                    >
                                        {signatureMethodLabels[method.signature_method] ?? method.signature_method}
                                    </Button>
                                ),
                            )}
                            {pendingMethod && (
                                <>
                                    <TextField>
                                        <TextField.Label>Kundens mobilnummer</TextField.Label>
                                        <TextField.Input
                                            type="tel"
                                            placeholder="F.eks. 12345678"
                                            value={phoneInput}
                                            onChange={(e) => setPhoneInput(e.target.value)}
                                        />
                                    </TextField>
                                    <Flex gap="100">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            style={{ width: "fit-content" }}
                                            loading={isRequesting}
                                            onClick={() => handleMethodSelect(pendingMethod)}
                                        >
                                            Send signeringsforespørsel
                                        </Button>
                                        <Button variant="text" size="sm" onClick={() => setPendingMethod(null)}>
                                            Avbryt
                                        </Button>
                                    </Flex>
                                </>
                            )}
                        </Flex>
                    )}

                {!!signCode && (
                    <Notification type="tile" variant="information" mt="100">
                        <Notification.Icon />
                        <Notification.Text>Signeringskode: {signCode}</Notification.Text>
                    </Notification>
                )}

                {manualSignClicked && (
                    <>
                        {assentlyDocuments && assentlyDocuments.length > 0 && (
                            <>
                                {hasPrinted ? (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        mt="200"
                                        style={{ width: "fit-content" }}
                                        onClick={() => setHasPrinted(false)}
                                    >
                                        Vis skriv-ut alternativer igjen
                                    </Button>
                                ) : (
                                    <>
                                        <p>Dokumenter tilgjengelig for signering:</p>
                                        <ul style={{ listStyle: "none", padding: 0 }}>
                                            {assentlyDocuments.map((doc) => (
                                                <li key={doc.id}>
                                                    <Checkbox>
                                                        <Checkbox.Trigger
                                                            checked={selectedDocIds.has(doc.id)}
                                                            onCheckedChange={() => {
                                                                setSelectedDocIds((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(doc.id)) {
                                                                        next.delete(doc.id);
                                                                    } else {
                                                                        next.add(doc.id);
                                                                    }
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                        <span>
                                                            <Icon
                                                                name="document-pdf"
                                                                size="sm"
                                                                aria-hidden="true"
                                                                focusable="false"
                                                            />
                                                            {DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type}
                                                        </span>
                                                    </Checkbox>
                                                </li>
                                            ))}
                                        </ul>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            disabled={selectedDocIds.size === 0}
                                            onClick={() => {
                                                const ids = Array.from(selectedDocIds).join(",");
                                                const newWindow = window.open(
                                                    `${API_URL_PRINT_SIGN_DOCUMENTS}?ids=${ids}&omOrderId=${omOrderId}`,
                                                    "_blank",
                                                    "noopener,noreferrer",
                                                );
                                                if (newWindow) {
                                                    newWindow.opener = null;
                                                }
                                                setHasPrinted(true);
                                            }}
                                        >
                                            <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                            Skriv ut valgte dokumenter ({selectedDocIds.size}/{assentlyDocuments.length}
                                            )
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                        {(!assentlyDocuments || assentlyDocuments.length === 0 || hasPrinted) && (
                            <Button
                                variant="primary"
                                size="sm"
                                mt="100"
                                style={{ width: "fit-content" }}
                                loading={isRequesting}
                                onClick={async () => {
                                    setIsRequesting(true);
                                    setError(null);
                                    try {
                                        const response = await fetch(API_URL_CONFIRM_MANUAL_SIGNATURE_FOR_ORDER, {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ omOrderId }),
                                        });
                                        if (!response.ok) {
                                            const body = await response.json().catch(() => ({}));
                                            throw new Error(body?.error ?? "Kunne ikke bekrefte signatur");
                                        }
                                        toast.success("Ordre er signert", { position: "top-center", duration: 8000 });
                                        onSuccess();
                                    } catch (e: unknown) {
                                        setError(
                                            e instanceof Error
                                                ? e.message
                                                : "Kunne ikke bekrefte signatur. Vennligst prøv igjen.",
                                        );
                                    } finally {
                                        setIsRequesting(false);
                                    }
                                }}
                            >
                                Bekreft her når ordre {omOrderId} er signert
                            </Button>
                        )}
                    </>
                )}

                {isSigningInProgress && !manualSignClicked && assentlyDocuments && assentlyDocuments.length > 0 && (
                    <>
                        <p>Dokumenter tilgjengelig for signering:</p>
                        <Flex direction="row" gap="200" align="flex-start">
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                {assentlyDocuments.map((doc) => (
                                    <li key={doc.id}>
                                        <Checkbox>
                                            <Checkbox.Trigger
                                                checked={selectedDocIds.has(doc.id)}
                                                onCheckedChange={() => {
                                                    setSelectedDocIds((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(doc.id)) {
                                                            next.delete(doc.id);
                                                        } else {
                                                            next.add(doc.id);
                                                        }
                                                        return next;
                                                    });
                                                }}
                                            />
                                            <span>
                                                <Icon
                                                    name="document-pdf"
                                                    size="sm"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                />
                                                {DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type}
                                            </span>
                                        </Checkbox>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                variant="secondary"
                                size="sm"
                                style={{ width: "fit-content", flexShrink: 0 }}
                                onClick={() => {
                                    const ids = Array.from(selectedDocIds).join(",");
                                    const newWindow = window.open(
                                        `${API_URL_PRINT_SIGN_DOCUMENTS}?ids=${ids}&omOrderId=${omOrderId}`,
                                        "_blank",
                                        "noopener,noreferrer",
                                    );
                                    if (newWindow) {
                                        newWindow.opener = null;
                                    }
                                    setHasOpenedDocuments(true);
                                }}
                                disabled={selectedDocIds.size === 0}
                            >
                                <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                Åpne dokumenter ({selectedDocIds.size}/{assentlyDocuments.length})
                            </Button>
                        </Flex>
                    </>
                )}

                {isAwaitingSigning &&
                    !signCode &&
                    !isTerminalSignStatus(signStatus) &&
                    (hasOpenedDocuments || !assentlyDocuments?.length) && (
                        <Notification type="tile" variant="information" mt="100">
                            <Notification.Icon />
                            <Notification.Text>Avventer signering fra kunde</Notification.Text>
                        </Notification>
                    )}

                {!!signStatus && (
                    <Notification type="tile" variant="information" mt="100">
                        <Notification.Icon />
                        <Notification.Text>
                            Status: {SIGN_STATUS_LABELS[signStatus.toLowerCase() as SHSignStatus] ?? signStatus}
                        </Notification.Text>
                    </Notification>
                )}

                {(!!signCode || isAwaitingSigning) &&
                    !isTerminalSignStatus(signStatus) &&
                    (hasOpenedDocuments || !assentlyDocuments?.length) && (
                        <Button
                            variant="secondary"
                            size="sm"
                            mt="100"
                            style={{ width: "fit-content" }}
                            onClick={async () => {
                                try {
                                    const url = new URL(
                                        API_URL_GET_SIGN_DOCUMENT_STATUS_FOR_ORDER,
                                        window.location.href,
                                    );
                                    url.searchParams.set("omOrderId", String(omOrderId));
                                    const response = await fetch(url.toString());
                                    if (!response.ok) throw new Error("Feil ved henting av signeringsstatus");
                                    const statusResult = await response.json();
                                    const newStatus = statusResult?.status;
                                    if (newStatus) {
                                        setSignStatus(newStatus);
                                        if (isTerminalSignStatus(newStatus)) {
                                            setIsAwaitingSigning(false);
                                            stopPolling();
                                            if (newStatus.toLowerCase() === "signed") {
                                                toast.success("Ordre er signert", {
                                                    position: "top-center",
                                                    duration: 8000,
                                                });
                                                onSuccess();
                                            }
                                        }
                                    }
                                } catch {
                                    setError("Kunne ikke hente signeringsstatus. Vennligst prøv igjen.");
                                }
                            }}
                        >
                            Sjekk status nå
                        </Button>
                    )}

                {isTerminalSignStatus(signStatus) && onRefresh && (
                    <Button variant="secondary" size="sm" mt="100" style={{ width: "fit-content" }} onClick={onRefresh}>
                        Oppdater tabell
                    </Button>
                )}

                <Flex gap="100" mt="100">
                    {isSigningInProgress && !isTerminalSignStatus(signStatus) && (
                        <Button variant="text" size="sm" onClick={handleBack}>
                            Tilbake
                        </Button>
                    )}
                    {!isTerminalSignStatus(signStatus) && !pendingMethod && (
                        <Button variant="text" size="sm" onClick={onCancel}>
                            Avbryt
                        </Button>
                    )}
                </Flex>
            </Flex>
        </div>
    );
};

export default SignOrderModal;
